"use client";
import { useState } from "react";
import clsx from "clsx";
import { ProductsAndServicesPageQueryResult } from "@/sanity.types";
import BadgeRoundedSquare from "../../../utils/badge-rounded-square/BadgeRoundedSquare";
import Arrow from "../../../utils/badge-rounded-square/icons-svg/Arrow";

type AccordionServicePageProps = {
	items: NonNullable<ProductsAndServicesPageQueryResult>["whatToKnowAboutUs"]["questionsAndAnswers"];
};

export default function AccordionServicePage({ items }: AccordionServicePageProps) {
	const [openItem, setOpenItem] = useState<string>(items[0]?.question || "");
	const [hoveredItem, setHoveredItem] = useState<string | null>(null);

	const toggleItem = (itemKey: string) => {
		if (openItem !== itemKey) {
			setOpenItem(itemKey);
		}
	};

	return (
		<div className="border-navy-blue-middle overflow-hidden rounded-[0.625rem] border">
			{items.map((item, index) => {
				const itemKey = item.question;
				const isOpen = openItem === itemKey;
				const isHovered = hoveredItem === itemKey;

				return (
					<div
						key={itemKey}
						className={clsx(
							"border-navy-blue-middle -mt-1 rounded-t-[0.625rem] border-t transition-all duration-500 ease-in-out",
							{
								"bg-white": isOpen,
								"bg-navy-blue-deep": !isOpen,
							},
						)}
						style={{
							zIndex: items.length - index,
						}}
					>
						<button
							className={clsx(
								"group flex w-full cursor-pointer items-baseline justify-between gap-x-2.5 px-5 py-5 text-left transition-all duration-500 ease-in-out sm:px-10 sm:pt-10",
								!isOpen && "pb-[3.438rem]",
								isOpen && "sm:pb-5",
							)}
							onClick={() => toggleItem(itemKey)}
							onMouseEnter={() => setHoveredItem(itemKey)}
							onMouseLeave={() => setHoveredItem(null)}
						>
							<span
								className={clsx(
									"text-h7-mob sm:text transition-colors duration-500 ease-in-out sm:leading-[130%] sm:font-medium sm:tracking-[0]",
									isOpen ? "text-graphite-black" : "text-white",
								)}
							>
								{item.question}
							</span>

							<BadgeRoundedSquare
								icon={
									<Arrow className="h-4 w-4 transition-transform duration-500 ease-in-out" rotation={isOpen ? 90 : -90} />
								}
								backgroundColor={clsx(isHovered || isOpen ? "bg-olive-green-light" : "bg-navy-blue-middle")}
								iconColor="text-navy-blue-deep"
								className="h-[1.875rem] w-[1.875rem] shrink-0 transition-all duration-500 ease-in-out sm:h-10 sm:w-10"
							/>
						</button>

						<div
							className={clsx(
								"transition-all duration-500 ease-in-out",
								isOpen ? "max-h-96 opacity-100" : "max-h-0 overflow-hidden opacity-0",
							)}
						>
							<div className="px-5 pb-7.5 sm:px-10 sm:pb-10">
								<div className="flex h-full items-start">
									<p className="text-body-s sm:text-body-m text-grey-600 xsm:max-w-[95%] max-w-[87.5%] whitespace-pre-wrap">
										{item.answer}
									</p>
								</div>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}
